import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';

import { ArrowLeftOutlined, CheckOutlined } from '@ant-design/icons';
import { Button } from '@mm/components';
import { Route, Switch, useHistory } from 'react-router-dom';

import { useXRayConfigurationCRUDActions } from '@mm/state/actions/XRayConfiguration';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import {
  hasConfiguredEvents,
  hasConfiguredFb,
  hasConfiguredIntegrations,
  selectXRayConfig,
} from '@mm/state/selectors/xrayConfigSelectors';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { EventsStep } from './EventsStep/EventsStep';
import { FacebookSettingsStep } from './FacebookSettingsStep/FacebookSettingsStep';
import { GoLiveStep } from './GoLiveStep/GoLiveStep';
import css from './SetupForm.module.css';

type SetupFormProps = {
  className?: string;
};

const steps = {
  events: {
    path: '',
    number: '1',
    title: 'Events',
    description: 'Select what events you want to send to Facebook',
  },
  fbSettings: {
    path: '/facebook-settings',
    title: 'Facebook Settings',
    description:
      'In order to send event data to Facebook, we need the following information from your Facebook account.',
    number: '2',
  },
  goLive: {
    path: '/start',
    title: 'Go Live',
    description:
      'We recommend testing your configuration before getting started, but you can continue without testing.',
    number: '3',
  },
};

export const emptyEventsConfig = {
  viewContent: '',
  viewItem: '',
  search: '',
  addToCart: '',
  initiateCheckout: '',
  addPaymentInfo: '',
  purchase: '',
  subscriptionPurchase: '',
  completeRegistration: '',
  subscribe: '',
};

export const facebookConversionApiEvents = [
  {
    key: 'viewContent',
    title: 'View Content',
    info: 'Standard Page view events should just include your website URL. Enter your website URL.',
    placeholder: 'Add URL',
  },
  {
    key: 'viewItem',
    title: 'View Item',
    info: (
      <>
        Enter the URL for a product view. Typically this is{' '}
        <strong>yourwebsite.com/product</strong>
      </>
    ),
    placeholder: 'Add URL',
  },
  {
    key: 'addToCart',
    title: 'Add to Cart',
    info: (
      <>
        Enter the URL for a cart view. Typically this is{' '}
        <strong>yourwebsite.com/cart</strong>
      </>
    ),
    placeholder: 'Add URL',
  },
  {
    key: 'initiateCheckout',
    title: 'Initiate Checkout',
    info: (
      <>
        Enter the URL for a checkout page. Typically this is{' '}
        <strong>yourwebsite.com/checkout</strong>
      </>
    ),
    placeholder: 'Add URL',
  },
  // {
  //   key: 'addPaymentInfo',
  //   title: 'Add Payment Info',
  //   info: '',
  //   placeholder: 'Add URL',
  // },
  {
    key: 'purchase',
    title: 'Purchase',
    info: (
      <>
        Enter the URL for a completed purchase. Typically this is{' '}
        <strong>yourwebsite.com/thankyou</strong>
      </>
    ),
    placeholder: 'Add URL',
  },
  // {
  //   key: 'subscriptionPurchase',
  //   title: 'Subscription Purchase',
  //   info: '',
  //   placeholder: 'Add URL',
  // },
  {
    key: 'search',
    title: 'Search',
    info: 'Enter the URL for a search result.',
    placeholder: 'Add URL',
  },
  {
    key: 'completeRegistration',
    title: 'Complete Registration',
    info: 'Enter the URL for a account sign up or account creation page.',
    placeholder: 'Add URL',
  },
  {
    key: 'activeOnSite',
    title: 'Active on Site',
  },
  // {
  //   key: 'subscribe',
  //   title: 'Subscribe',
  //   info: '',
  //   placeholder: 'Add URL',
  // },
];

export const facebookConversionApiVisibleEventsIds =
  facebookConversionApiEvents.map(v => v.key);

export const SetupForm = ({ className }: SetupFormProps) => {
  const [configReady, setConfigReady] = useState(false);

  const history = useHistory();
  const match = useRouteMatch<{ step?: string; chatbotId: string }>();

  const xRayConfigurationCRUDActions = useXRayConfigurationCRUDActions();

  const chatbotId = useSelector(activeChatbotIdSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const defaultConfiguration = useSelector(selectXRayConfig);

  const currentStep = Object.values(steps).findIndex(step =>
    step.path.includes(match.params.step || ''),
  );

  const containerClassName = classNames(className, css.container);

  const isDisabled = useCallback(
    step => {
      switch (step.path) {
        case steps.fbSettings.path: {
          return (
            !(
              hasConfiguredEvents(defaultConfiguration) ||
              hasConfiguredIntegrations(defaultConfiguration)
            ) && !hasConfiguredFb(defaultConfiguration)
          );
        }
        case steps.goLive.path: {
          return (
            !(
              hasConfiguredEvents(defaultConfiguration) ||
              hasConfiguredIntegrations(defaultConfiguration)
            ) && !hasConfiguredFb(defaultConfiguration)
          );
        }
        default:
          return false;
        // return Object.values(steps).findIndex(v => v.path === step.path) > currentStep
      }
    },
    [defaultConfiguration, configReady],
  );
  const isDone = useCallback(
    step => {
      switch (step.path) {
        case steps.events.path: {
          return (
            hasConfiguredEvents(defaultConfiguration) ||
            hasConfiguredIntegrations(defaultConfiguration)
          );
        }
        case steps.fbSettings.path: {
          return hasConfiguredFb(defaultConfiguration);
        }
        case steps.goLive.path: {
          return !!defaultConfiguration?.active;
        }
        default:
          return false;
        // return Object.values(steps).findIndex(v => v.path === step.path) < currentStep
      }
    },
    [configReady, defaultConfiguration],
  );

  const isActive = useCallback(
    step =>
      Object.values(steps).findIndex(v => v.path === step.path) === currentStep,
    [configReady, defaultConfiguration, currentStep],
  );

  useEffect(() => {
    (async () => {
      try {
        await xRayConfigurationCRUDActions.getXRayEventsConfiguration({
          botId: chatbotId,
        });
      } finally {
        setConfigReady(true);
      }
    })();
  }, [chatbotId]);

  return (
    <div className={containerClassName}>
      <div className={css.sidebar}>
        <Button
          css={{ container: css.backBtn }}
          color="transparent"
          onClick={() =>
            history.push(BotBuilderRoutes.restore.getLink({ chatbotId }), {
              options: 'facebook_ads_audience',
            })
          }
        >
          <ArrowLeftOutlined />
          Back to Restore
        </Button>

        <span className={css.sidebarTitle}>Facebook CAPI Setup Steps</span>
        <span className={css.sidebarSubtitle}>Send data to Facebook...</span>

        <ul
          className={classNames(css.stepMenu, {
            [css.disabled]: !configReady,
          })}
        >
          {Object.values(steps).map((step, idx) => (
            <li
              key={step.title}
              className={classNames(css.stepMenuItem, {
                [css.disabled]: isDisabled(step),
                [css.done]: isDone(step),
                [css.active]: isActive(step),
              })}
              onClick={
                isDisabled(step)
                  ? undefined
                  : () => {
                      history.push(
                        match.path
                          .replace(':chatbotId', match.params.chatbotId)
                          .replace(':step?', step.path.replace('/', '')),
                      );
                    }
              }
            >
              <span className={css.stepNumber}>
                {isDone(step) ? <CheckOutlined /> : step.number}
              </span>
              <span className={css.stepTitle}>{step.title}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={css.stepContent}>
        <div className={css.stepContentHeader}>
          <div className={css.stepContentHeaderText}>
            <span className={css.stepContentTitle}>
              {Object.values(steps)[currentStep].title}
            </span>
            <span className={css.stepContentDescription}>
              {Object.values(steps)[currentStep].description}
            </span>
          </div>
          <div className={css.stepActions}>
            <a
              className={css.howToLink}
              href={window.location.href}
              onClick={async e => {
                e.preventDefault();

                // eslint-disable-next-line no-alert
                const shouldReset = window?.confirm?.('Are you sure?');

                if (!shouldReset) {
                  return;
                }

                try {
                  await xRayConfigurationCRUDActions.updateXRayEventsConfiguration(
                    chatbotId,
                    {
                      facebookPageId,
                      configurations: emptyEventsConfig,
                      pixelId: '',
                      accessToken: '',
                      active: false,
                    },
                  );
                  window.location.href = BotBuilderRoutes.capi.getLink({
                    chatbotId,
                  });
                  // eslint-disable-next-line no-empty
                } finally {
                }
              }}
            >
              Reset
            </a>

            <a
              href="https://www.facebook.com/business/help/2041148702652965?id=818859032317965"
              target="_blank"
              className={css.howToLink}
              rel="noreferrer"
            >
              How does Facebook CAPI work?
            </a>
          </div>
        </div>

        {configReady && (
          <Switch>
            <Route
              path={match.path + steps.goLive.path}
              component={GoLiveStep}
            />
            <Route
              path={match.path + steps.fbSettings.path}
              component={FacebookSettingsStep}
            />
            <Route
              path={match.path + steps.events.path}
              exact
              component={EventsStep}
            />
          </Switch>
        )}
      </div>
    </div>
  );
};
